import React, { useMemo, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { getAllProviderSettings } from '../../Services/ProviderService';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import DocumentUpload from '../Common/DocumentUpload';
import CustomDatePicker from '../Common/CustomDatePicker';

const validationSchema = Yup.object({
  id: Yup.string().nullable(),
  provider_id: Yup.string().nullable(),
  organization_id: Yup.string().nullable(),
  status_id: Yup.number().nullable().transform((value, originalValue) => (originalValue === '' ? null : Number(originalValue))),
  file_id: Yup.number().nullable(),
  exam_type: Yup.number().nullable().transform((value, originalValue) => (originalValue === '' ? null : Number(originalValue))),
  first_try_date: Yup.date().nullable().typeError('Invalid date format'),
  exam_date: Yup.date().nullable().typeError('Invalid date format'),
  exam_passed: Yup.boolean().nullable(),
  score: Yup.string().trim().nullable(),
  no_of_attempts: Yup.number().nullable(),
  location: Yup.string().trim().nullable(),
  type: Yup.string().trim().nullable(),
  document: Yup.mixed().nullable(),
});

const emptyForm = {
  id: null,
  provider_id: '',
  organization_id: '',
  status_id: '',
  file_id: null,
  exam_type: '',
  first_try_date: null,
  exam_date: null,
  exam_passed: '',
  score: '',
  no_of_attempts: null,
  location: '',
  type: 'create',
  document: null,
};

const STATUS_OPTIONS = [
  { id: 1, label: 'Active' },
  { id: 2, label: 'Inactive' },
];

const EXAM_TYPE_SETTING_ID = 23;

const mapProviderSettingItem = (item) => ({
  value: item.id ?? item.Id ?? item.value ?? item.value_id ?? null,
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
});

export default function ExamModal({ isOpen, onClose, onSave, initialData, isLoading, providerId, organizationId }) {
  const { token } = useAuth();
  const [examTypeOptions, setExamTypeOptions] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const loadExamTypeSettings = async () => {
    if (!token) return;
    setSettingsLoading(true);

    try {
      const response = await getAllProviderSettings(token, EXAM_TYPE_SETTING_ID);
      if (response?.is_success) {
        setExamTypeOptions(Array.isArray(response.data) ? response.data.map(mapProviderSettingItem) : []);
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to load exam type settings');
      }
    } catch (error) {
      console.error('Failed to load exam type settings:', error);
      toast.error('Failed to load exam type settings');
      setExamTypeOptions([]);
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    loadExamTypeSettings();
  }, [token]);

  const initialValues = useMemo(() => {
    const baseValues = initialData ? { ...emptyForm, ...initialData } : { ...emptyForm };
    return {
      ...baseValues,
      provider_id: providerId || baseValues.provider_id,
      organization_id: organizationId || baseValues.organization_id,
      type: baseValues.id ? 'update' : 'create',
    };
  }, [initialData, providerId, organizationId]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        id: values.id || null,
        provider_id: providerId || values.provider_id || null,
        organization_id: organizationId || values.organization_id || null,
        exam_type: values.exam_type !== '' && values.exam_type !== null && values.exam_type !== undefined ? Number(values.exam_type) : null,
        status_id: values.status_id !== '' && values.status_id !== null && values.status_id !== undefined ? Number(values.status_id) : null,
        no_of_attempts: values.no_of_attempts !== '' && values.no_of_attempts !== null && values.no_of_attempts !== undefined ? Number(values.no_of_attempts) : null,
        exam_passed: values.exam_passed === true || values.exam_passed === 'true' ? true : values.exam_passed === false || values.exam_passed === 'false' ? false : null,
        first_try_date: values.first_try_date || null,
        exam_date: values.exam_date || null,
        type: values.id ? 'update' : 'create',
      };
      onSave(payload);
    },
  });

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered ">
        <div className="modal-content border-0 rounded-4">
          <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold hc-text-primary m-0">{initialData?.id ? 'Edit Exam' : 'Add Exam'}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading}>
            </button>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body px-4 py-2">
              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <div className="row g-3">
                  <div className="col-12">
                    <h6 className="fw-bold hc-text-primary mb-3">Exam Details</h6>
                  </div>

                  <div className="col-md-6">
                    <SearchableDropdown
                      label="Exam Type"
                      placeholder="Select Option"
                      options={examTypeOptions.map((option) => ({
                        value: option.value,
                        label: option.label,
                      }))}
                      value={formik.values.exam_type}
                      onChange={(value) => {
                        formik.setFieldValue('exam_type', value || '');
                        formik.setFieldTouched('exam_type', true);
                      }}
                      loading={settingsLoading}
                      disabled={settingsLoading}
                      className={formik.touched.exam_type && formik.errors.exam_type ? 'is-invalid' : ''}
                      emptyMessage="No exam types found"
                    />
                    {formik.touched.exam_type && formik.errors.exam_type ? (
                      <div className="invalid-feedback d-block">{formik.errors.exam_type}</div>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Score</label>
                    <input
                      name="score"
                      className={`form-control rounded-3 shadow-sm ${formik.touched.score && formik.errors.score ? 'is-invalid' : ''}`}
                      value={formik.values.score}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">First Try Date</label>
                    {/* <input
                      type="date"
                      name="first_try_date"
                      className={`form-control rounded-3 shadow-sm ${formik.touched.first_try_date && formik.errors.first_try_date ? 'is-invalid' : ''}`}
                      value={formik.values.first_try_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    /> */}

                    <CustomDatePicker
                      name="first_try_date"
                      className={`form-control rounded-3 shadow-sm ${formik.touched.first_try_date && formik.errors.first_try_date ? 'is-invalid' : ''}`}
                      formik={formik}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Exam Date</label>
                     <CustomDatePicker
                      formik={formik}
                      name="exam_date"
                      className={`form-control rounded-3 shadow-sm ${formik.touched.exam_date && formik.errors.exam_date ? 'is-invalid' : ''}`}
                     />
                  </div> 

                  <div className="col-md-12 col-lg-4">
                    <label className="form-label fw-semibold">No. of Attempts</label>
                    <input
                      type="number"
                      name="no_of_attempts"
                      className={`form-control rounded-3 shadow-sm ${formik.touched.no_of_attempts && formik.errors.no_of_attempts ? 'is-invalid' : ''}`}
                      value={formik.values.no_of_attempts}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>

                  <div className="col-md-12 col-lg-4">
                    <label className="form-label fw-semibold">Exam Passed</label>
                    <select
                      name="exam_passed"
                      className={`form-select rounded-3 shadow-sm ${formik.touched.exam_passed && formik.errors.exam_passed ? 'is-invalid' : ''}`}
                      value={formik.values.exam_passed ? 'true' : formik.values.exam_passed === false ? 'false' : ''}
                      onChange={(e) => formik.setFieldValue('exam_passed', e.target.value === 'true')}
                      onBlur={formik.handleBlur}
                    >
                      <option value="">Select Option</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div className="col-md-12 col-lg-4">
                    <label className="form-label fw-semibold">Status</label>
                    <select
                      name="status_id"
                      className={`form-select rounded-3 shadow-sm ${formik.touched.status_id && formik.errors.status_id ? 'is-invalid' : ''}`}
                      value={formik.values.status_id}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value="">Select Option</option>
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Location</label>
                    <input
                      name="location"
                      className={`form-control rounded-3 shadow-sm ${formik.touched.location && formik.errors.location ? 'is-invalid' : ''}`}
                      value={formik.values.location}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <div className="row g-3">
                  <div className="col-12">
                    <h6 className="fw-bold hc-text-primary mb-3">Document</h6>
                  </div>
                  <div className="col-12">
                    <DocumentUpload
                      id="exam-document-input"
                      label="Upload Document"
                      value={formik.values.document}
                      onChange={(file) => formik.setFieldValue('document', file)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light rounded-3 px-4" onClick={onClose} disabled={isLoading || formik.isSubmitting}>Cancel</button>
              <button type="submit" className="btn hc-bg-primary text-white rounded-3 px-4" disabled={isLoading || formik.isSubmitting || !formik.isValid}>
                {isLoading || formik.isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
